import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getDelegaStats, getDelegaHistory } from "../../api/client";
import type { DelegaStats, DelegaHistoryItem, FirmaStatusCode } from "../../api/types";

function fmt(iso?: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function FirmaChip({ status }: { status: FirmaStatusCode }) {
  if (!status) return <span style={{ color: "var(--indaco-gray-400)", fontSize: "var(--font-size-xs)" }}>—</span>;
  const MAP: Record<string, { label: string; bg: string; color: string }> = {
    attesa_delegante:  { label: "Attesa delegante", bg: "var(--indaco-info-light)", color: "var(--indaco-info)" },
    attesa_delegato:   { label: "Attesa delegato",  bg: "var(--indaco-info-light)", color: "var(--indaco-info)" },
    completato:        { label: "Firmato ✅",         bg: "var(--indaco-success-light)", color: "var(--indaco-success)" },
    errore:            { label: "Errore ❌",           bg: "var(--indaco-danger-light)",  color: "var(--indaco-danger)" },
  };
  const s = MAP[status] ?? { label: status, bg: "var(--indaco-gray-100)", color: "var(--indaco-gray-600)" };
  return (
    <span style={{ display: "inline-flex", alignItems: "center", padding: "1px 8px", borderRadius: 99, fontSize: "var(--font-size-xs)", fontWeight: "var(--font-weight-medium)", background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
}

export function DelegheStats() {
  const navigate = useNavigate();
  const [stats, setStats]   = useState<DelegaStats | null>(null);
  const [items, setItems]   = useState<DelegaHistoryItem[]>([]);
  const [total, setTotal]   = useState(0);
  const [page, setPage]     = useState(0);
  const PAGE = 20;
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError]   = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getDelegaStats(),
      getDelegaHistory(PAGE, 0),
    ]).then(([s, h]) => {
      if (cancelled) return;
      setStats(s);
      setItems(h.items);
      setTotal(h.total);
    }).catch((e: unknown) => {
      if (!cancelled) setError(e instanceof Error ? e.message : "Errore sconosciuto");
    }).finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, []);

  async function loadPage(p: number) {
    try {
      const h = await getDelegaHistory(PAGE, p * PAGE);
      setItems(h.items);
      setPage(p);
    } catch { /* ignore */ }
  }

  if (isLoading) return <div className="loading-center" style={{ minHeight: 400 }}><div className="spinner spinner-lg" /></div>;

  if (error || !stats) return (
    <div className="card" style={{ background: "var(--indaco-danger-light)", color: "var(--indaco-danger)" }}>
      <span className="material-symbols-outlined" style={{ fontSize: 16, marginRight: 4 }}>error</span>
      Errore: {error || "Dati non disponibili"}
    </div>
  );

  const legittimatiPct = stats.total > 0 ? Math.round((stats.legittimati / stats.total) * 100) : 0;
  type KpiItem = { label: string; value: string; color?: string };
  const kpiItems: KpiItem[] = [
    { label: "Totale verifiche",     value: String(stats.total),          color: "var(--indaco-brand-dark)" },
    { label: "Completate",           value: String(stats.completed),      color: "var(--indaco-gray-700)" },
    { label: "✅ Legittimati",        value: String(stats.legittimati),    color: "var(--indaco-success)" },
    { label: "❌ Non legittimati",    value: String(stats.non_legittimati), color: "var(--indaco-danger)" },
    { label: "Tasso OK",             value: `${legittimatiPct}%`,          color: "var(--indaco-brand-primary)" },
    ...(stats.avg_cost_usd != null ? [{ label: "Costo medio", value: `$${stats.avg_cost_usd.toFixed(4)}`, color: "var(--indaco-gray-600)" }] : []),
  ];

  const totalPages = Math.ceil(total / PAGE);

  return (
    <div>
      <h1 className="page-title" style={{ marginBottom: "var(--space-6)" }}>
        <span className="material-symbols-outlined" style={{ marginRight: "var(--space-2)" }}>analytics</span>
        Deleghe — Statistiche &amp; Storico
      </h1>

      {/* KPI */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: "var(--space-4)", marginBottom: "var(--space-8)" }}>
        {kpiItems.map((k, i) => (
          <div key={i} className="card" style={{ textAlign: "center" }}>
            <div className="text-sm text-muted" style={{ marginBottom: "var(--space-1)" }}>{k.label}</div>
            <div style={{ fontSize: "var(--font-size-2xl)", fontWeight: "var(--font-weight-bold)", color: k.color }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Storico */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "var(--space-4)" }}>
        <h2 style={{ margin: 0, fontSize: "var(--font-size-lg)", fontWeight: "var(--font-weight-semibold)" }}>
          Storico verifiche
        </h2>
        <button className="btn btn-primary btn-sm" onClick={() => navigate("/deleghe")}>
          <span className="material-symbols-outlined" style={{ fontSize: 16 }}>add</span>
          Nuova verifica
        </button>
      </div>

      {items.length === 0 ? (
        <div className="card" style={{ textAlign: "center", padding: "var(--space-8)" }}>
          <span className="material-symbols-outlined" style={{ fontSize: 48, display: "block", marginBottom: "var(--space-3)", color: "var(--indaco-gray-400)" }}>history</span>
          <p className="text-muted">Nessuna verifica effettuata ancora.</p>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  {["Data", "Delegante", "Delegato", "Verdetto", "Firma", "PDF"].map(h => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <tr key={item.id} onClick={() => navigate(`/deleghe/${item.id}`)}>
                    <td style={{ whiteSpace: "nowrap", color: "var(--indaco-gray-500)" }}>{fmt(item.created_at)}</td>
                    <td>{item.delegante_nome} {item.delegante_cognome}</td>
                    <td>{item.delegato_nome} {item.delegato_cognome}</td>
                    <td>
                      {item.verdetto_stato === "LEGITTIMATO"
                        ? <span style={{ color: "var(--indaco-success)", fontWeight: "var(--font-weight-bold)" }}>✅</span>
                        : item.verdetto_stato === "NON_LEGITTIMATO"
                          ? <span style={{ color: "var(--indaco-danger)", fontWeight: "var(--font-weight-bold)" }}>❌</span>
                          : <span style={{ color: "var(--indaco-gray-400)" }}>—</span>
                      }
                    </td>
                    <td><FirmaChip status={item.firma_status} /></td>
                    <td>
                      {item.has_documento
                        ? <span className="material-symbols-outlined" style={{ fontSize: 18, color: "var(--indaco-brand-primary)" }}>picture_as_pdf</span>
                        : <span style={{ color: "var(--indaco-gray-300)" }}>—</span>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginazione */}
          {totalPages > 1 && (
            <div className="pagination">
              <button disabled={page === 0} onClick={() => loadPage(page - 1)}>
                <span className="material-symbols-outlined icon-sm">chevron_left</span>
              </button>
              <span style={{ fontSize: "var(--font-size-sm)", color: "var(--indaco-gray-600)" }}>
                Pagina {page + 1} di {totalPages} ({total} totali)
              </span>
              <button disabled={page >= totalPages - 1} onClick={() => loadPage(page + 1)}>
                <span className="material-symbols-outlined icon-sm">chevron_right</span>
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
